import React, {FC, useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {
    Box,
    Button,
    Flex,
    ListItem,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Text,
    UnorderedList,
    VStack,
    useToast,
} from "@chakra-ui/react";
import {InactiveOrderReminderController} from "../../controllers/InactiveOrderReminderController";
import {InactiveDealershipItem, SendInactiveOrderReminderBatch} from "../../shared/entities/Order/InactiveOrderReminder";
import {buildRecipientEmails} from "../../shared/entities/Order/InactiveOrderReminderRecipients";
import {InactiveOrderReminderDealershipSettings} from "../../shared/entities/Order/InactiveOrderReminder";

interface InactiveOrderReminderConfirmSendModalProps {
    isOpen: boolean;
    onClose: () => void;
    dealerships: InactiveDealershipItem[];
    monthsThreshold: number;
    onSuccess?: () => void;
}

export const InactiveOrderReminderConfirmSendModal: FC<InactiveOrderReminderConfirmSendModalProps> = ({
    isOpen,
    onClose,
    dealerships,
    monthsThreshold,
    onSuccess,
}) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const controller = new InactiveOrderReminderController(dispatch);
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(false);
    const [settingsByDealer, setSettingsByDealer] = useState<Record<number, InactiveOrderReminderDealershipSettings>>({});

    useEffect(() => {
        if (!isOpen || dealerships.length === 0) {
            return;
        }
        setLoading(true);
        controller
            .settings(dealerships.map((d) => d.dealershipId))
            .then((rows) => {
                const map: Record<number, InactiveOrderReminderDealershipSettings> = {};
                (rows || []).forEach((r) => {
                    map[r.dealershipId] = r;
                });
                setSettingsByDealer(map);
            })
            .finally(() => setLoading(false));
    }, [isOpen, dealerships]);

    const preview = dealerships
        .map((dealer) => {
            const config = settingsByDealer[dealer.dealershipId];
            const emails = config ? buildRecipientEmails(config) : [];
            return {dealer, emails};
        })
        .filter((p) => p.emails.length > 0);

    const handleSend = async () => {
        if (preview.length === 0) {
            toast({
                title: "Configure email recipients first (settings button)",
                status: "warning",
                duration: 4000,
                isClosable: true,
            });
            return;
        }
        const payload: SendInactiveOrderReminderBatch = {
            monthsThreshold,
            items: preview.map(({dealer, emails}) => ({
                dealershipId: dealer.dealershipId,
                dealershipName: dealer.dealershipName,
                productIds: dealer.products
                    .filter((p) => !p.reminderExcluded)
                    .map((p) => p.productId),
                recipients: emails,
            })),
        };
        setSending(true);
        const result = await controller.send(payload);
        setSending(false);
        if (result?.None) {
            toast({title: "Failed to send reminders", status: "error", duration: 5000, isClosable: true});
            return;
        }
        toast({
            title: `Reminder sent to ${payload.items.length} dealership(s)`,
            status: "success",
            duration: 4000,
            isClosable: true,
        });
        onSuccess?.();
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>Send order reminder</ModalHeader>
                <ModalCloseButton/>
                <ModalBody>
                    {loading ? (
                        <Flex justify="center" py={8}><Spinner/></Flex>
                    ) : (
                        <>
                            <Text fontSize="13px" mb={4} color="gray.600">
                                The reminder will be sent to the following addresses. Sent products are hidden for 9 months.
                            </Text>
                            <VStack align="stretch" spacing={4}>
                                {preview.map(({dealer, emails}) => (
                                    <Box key={dealer.dealershipId} borderWidth="1px" borderRadius="md" p={3}>
                                        <Text fontWeight="bold" fontSize="13px" mb={2}>
                                            {dealer.dealershipName || `Dealership #${dealer.dealershipId}`}
                                        </Text>
                                        <UnorderedList spacing={1} pl={4} style={{fontSize: "13px"}}>
                                            {emails.map((email) => (
                                                <ListItem key={email}>{email}</ListItem>
                                            ))}
                                        </UnorderedList>
                                    </Box>
                                ))}
                                {preview.length === 0 && (
                                    <Text fontSize="13px" color="orange.500">
                                        No recipients configured. Open settings (gear icon) to select emails.
                                    </Text>
                                )}
                            </VStack>
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} size="sm" onClick={onClose} isDisabled={sending || loading}>
                        Cancel
                    </Button>
                    <Button
                        colorScheme="blue"
                        size="sm"
                        onClick={handleSend}
                        isLoading={sending}
                        isDisabled={loading || preview.length === 0}
                    >
                        Send
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default InactiveOrderReminderConfirmSendModal;
